import type { ReactNode } from "react"
import { useCallback, useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type { DeliverableType } from "./deliverable-format-utils"
import type { PlanDecision } from "./plan-decision-types"
import type { DeepFile } from "@/stores/ai-explorer-store"

import { FileText, HelpCircle, Loader2, Pencil, SendHorizonal } from "@/components/icons"
import { MarkdownRenderer } from "@/components/markdown-renderer"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { Textarea } from "@/components/ui/textarea"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

import { DeliverableView } from "./DeliverableView"
import { FileDiffView } from "./FileDiffView"
import {
  EXECUTION_MODES,
  type ExecutionMode,
  parseExecutionMode,
  removePlanSource,
  setExecutionMode,
} from "./plan-metadata"
import { PlanBriefHeader } from "./PlanBriefHeader"
import { PlanEditorModal } from "./PlanEditorModal"
import { ToolResultView } from "./ToolResultView"
import {
  isDeliverableFile,
  isMarkdownFile,
  isPlanFile,
  looksLikeToolResult,
} from "./virtual-file-viewer-utils"

interface VirtualFileViewerProps {
  file: DeepFile | null
  /** When true, shows edit toggle + approve/reject buttons at the bottom */
  planProposed?: boolean
  /** UI-7: invoked when user clicks "Run in background" on the approval
   *  bar. Receives the same edited content / mode bundle as approve. */
  onApproveInBackground?: (extra?: { editedContent?: string }) => void
  /** When true and no file exists, shows a skeleton loading state */
  isStreaming?: boolean
  /** Deliverable format key (e.g. "memo", "comparison_table") — overrides
   *  the type parsed from file content. Comes from the backend
   *  deep_deliverable_type frame. */
  deliverableType?: DeliverableType | null
  onPlanDecision?: (
    decision: PlanDecision,
    extra?: { editedContent?: string; feedback?: string }
  ) => void
  /** Forwarded to DeliverableView — invoked when user clicks the sources
   *  badge and no References section exists in the report. */
  onSourcesRequested?: () => void
  /** Forwarded to DeliverableView — invoked with the raw citation string
   *  when the user clicks an inline citation badge. */
  onCitationClick?: (citation: string) => void
  /** Forwarded to DeliverableView — search-service / index id used by the
   *  in-deliverable hover-peek when building citation file URLs. */
  citationSection?: string
  /** Forwarded to DeliverableView — invoked when the user clicks "Open"
   *  inside the citation hover-peek. */
  onCitationViewDocument?: (
    citation: string,
    citationSection: string,
    options?: { chunkText?: string }
  ) => void
  /** Extra file-level actions rendered in the deliverable header. */
  headerActions?: ReactNode
}

type ViewMode = "latest" | "diff"

export function VirtualFileViewer({
  file,
  planProposed = false,
  isStreaming = false,
  deliverableType = null,
  onPlanDecision,
  onApproveInBackground,
  onSourcesRequested,
  onCitationClick,
  citationSection,
  onCitationViewDocument,
  headerActions,
}: VirtualFileViewerProps) {
  const { t } = useTranslation()
  const [viewMode, setViewMode] = useState<ViewMode>("latest")
  const [editorOpen, setEditorOpen] = useState(false)
  const [reviseMode, setReviseMode] = useState(false)
  const [revisePending, setRevisePending] = useState(false)
  const [reviseFeedback, setReviseFeedback] = useState("")
  const reviseInputRef = useRef<HTMLTextAreaElement>(null)
  // Snapshot of file.content at the moment revise was submitted — lets the
  // cleanup effect below tell "revised plan arrived" (content changed) apart
  // from "still the old plan" (content unchanged, which is true for the
  // entire replanning window while revisePending should stay set).
  const contentAtReviseRef = useRef<string | null>(null)
  // UI-4: structured edits the user makes in the brief / dropdown before
  // approving. ``null`` means "no edits, send the plan as-is"; any non-null
  // value is forwarded as ``editedContent`` on approve. Reset whenever the
  // underlying plan content changes (a fresh proposal, or a revise round).
  const [planEdits, setPlanEdits] = useState<string | null>(null)
  // ``executionModeOverride`` is the user's choice in the dropdown — null
  // means "respect what the agent picked." Kept separate from planEdits so
  // we can reset cleanly without rebuilding the markdown each render.
  const [executionModeOverride, setExecutionModeOverride] = useState<ExecutionMode | null>(null)

  // Clear revising state when plan is dismissed or file content changes (revised plan arrived)
  useEffect(() => {
    if (!planProposed) {
      setRevisePending(false)
      setReviseMode(false)
      setPlanEdits(null)
      setExecutionModeOverride(null)
    }
  }, [planProposed])

  // Reset edits whenever a fresh plan content arrives (revise-loop, new turn).
  useEffect(() => {
    setPlanEdits(null)
    setExecutionModeOverride(null)
  }, [file?.path])

  useEffect(() => {
    if (revisePending && file?.content && file.content !== contentAtReviseRef.current) {
      setRevisePending(false)
    }
  }, [file?.content, revisePending])

  const handleReviseSubmit = useCallback(() => {
    if (!reviseFeedback.trim()) return
    onPlanDecision?.("revise", { feedback: reviseFeedback.trim() })
    setReviseFeedback("")
    setReviseMode(false)
    contentAtReviseRef.current = file?.content ?? null
    setRevisePending(true)
  }, [reviseFeedback, onPlanDecision, file?.content])

  if (!file) {
    if (isStreaming) {
      return (
        <div className="flex flex-1 flex-col items-center justify-start gap-3 px-6 pt-8 pb-6 text-center">
          <Loader2 className="text-primary size-6 animate-spin" />
          <p className="text-muted-foreground text-sm">{t("deepExplorer.agent_working")}</p>
          <div className="mt-2 w-full max-w-xs space-y-2">
            <Skeleton className="h-3" />
            <Skeleton className="h-3 w-4/5" />
            <Skeleton className="h-3 w-3/5" />
          </div>
        </div>
      )
    }
    return (
      <div
        aria-busy="true"
        aria-label={t("deepExplorer.virtual_file_viewer_aria")}
        className="flex flex-1 flex-col items-center justify-start gap-3 px-4 pt-8 pb-4 text-center"
      >
        <FileText className="text-muted-foreground/60 size-8" />
        <p className="text-muted-foreground text-sm">
          {t("deepExplorer.no_file_open")} {t("deepExplorer.no_file_hint")}
        </p>
      </div>
    )
  }

  if (revisePending) {
    return (
      <div className="flex flex-1 flex-col overflow-auto p-4">
        <div className="flex flex-col items-center justify-center gap-3 py-8">
          <Loader2 className="text-primary size-6 animate-spin" />
          <p className="text-muted-foreground text-sm">{t("deepExplorer.revising_plan")}</p>
          <div className="w-full max-w-xs space-y-2">
            <Skeleton className="h-3" />
            <Skeleton className="h-3 w-4/5" />
          </div>
        </div>
        <div className="mt-4 opacity-50">
          <MarkdownRenderer content={file.content} />
        </div>
      </div>
    )
  }

  const hasDiff = Boolean(file.previous_content)
  const filename = file.path.split("/").pop() ?? file.path

  const renderContent = () => {
    if (viewMode === "diff" && hasDiff) {
      return <FileDiffView file={file} />
    }

    if (isDeliverableFile(file.path)) {
      return (
        <DeliverableView
          citationSection={citationSection}
          deliverableType={deliverableType}
          file={file}
          headerActions={headerActions}
          isStreaming={isStreaming}
          onCitationClick={onCitationClick}
          onCitationViewDocument={onCitationViewDocument}
          onSourcesRequested={onSourcesRequested}
        />
      )
    }

    if (isMarkdownFile(file.path)) {
      return (
        <div className="p-4">
          <MarkdownRenderer
            className="prose-sm max-w-none"
            content={isPlanFile(file.path) ? (planEdits ?? file.content) : file.content}
          />
        </div>
      )
    }

    if (looksLikeToolResult(file.path, file.content)) {
      return <ToolResultView content={file.content} />
    }

    return (
      <pre className="p-4 text-xs leading-relaxed">
        <code className="text-foreground font-mono break-words whitespace-pre-wrap">
          {file.content}
        </code>
      </pre>
    )
  }

  return (
    <section
      aria-label={t("deepExplorer.virtual_file_aria", { filename })}
      className="flex flex-1 flex-col overflow-hidden"
    >
      {/* Toolbar — only visible when there are actions (diff toggle or edit) */}
      {(hasDiff || planProposed) && (
        <div className="border-border flex shrink-0 items-center justify-end gap-2 border-b px-3 py-1">
          {planProposed && (
            <button
              className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs transition-colors"
              type="button"
              onClick={() => setEditorOpen(true)}
            >
              <Pencil className="size-3" />
              {t("deepExplorer.edit")}
            </button>
          )}
          {hasDiff && (
            <div
              aria-label={t("deepExplorer.view_mode_aria")}
              className="border-border flex items-center rounded-md border text-xs"
              role="tablist"
            >
              <button
                aria-selected={viewMode === "latest"}
                role="tab"
                type="button"
                className={cn(
                  "focus-visible:ring-ring rounded-l-md px-2 py-1 transition-colors focus-visible:ring-2",
                  viewMode === "latest"
                    ? "bg-primary/10 text-primary font-medium"
                    : "text-muted-foreground hover:text-foreground"
                )}
                onClick={() => setViewMode("latest")}
              >
                {t("deepExplorer.latest")}
              </button>
              <button
                aria-selected={viewMode === "diff"}
                role="tab"
                type="button"
                className={cn(
                  "focus-visible:ring-ring rounded-r-md px-2 py-1 transition-colors focus-visible:ring-2",
                  viewMode === "diff"
                    ? "bg-primary/10 text-primary font-medium"
                    : "text-muted-foreground hover:text-foreground"
                )}
                onClick={() => setViewMode("diff")}
              >
                {t("deepExplorer.diff")}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Plan brief stays visible after approval so the execution contract remains inspectable. */}
      {isPlanFile(file.path) && viewMode === "latest" && (
        <PlanBriefHeader
          deliverableType={deliverableType}
          planContent={planEdits ?? file.content}
          onRemoveSource={
            planProposed
              ? (source) => {
                  // Build off the current edits (or the original) so multiple
                  // removals chain cleanly. removePlanSource is a no-op when
                  // the source isn't found, so duplicate clicks are safe.
                  const base = planEdits ?? file.content
                  setPlanEdits(removePlanSource(base, source))
                }
              : undefined
          }
        />
      )}

      {/* Content area */}
      <div className="min-h-0 flex-1 overflow-auto">{renderContent()}</div>

      {/* Inline approval bar — shown at bottom when plan is proposed */}
      {planProposed && (
        <div className="border-border bg-card shrink-0 border-t">
          {/* Revise feedback input */}
          {reviseMode && (
            <div className="flex items-start gap-2 px-4 pt-3">
              <Textarea
                ref={reviseInputRef}
                className="min-h-0 flex-1 resize-none overflow-hidden px-3 py-1.5 text-sm"
                placeholder={t("deepExplorer.revise_placeholder")}
                rows={1}
                value={reviseFeedback}
                onChange={(e) => {
                  setReviseFeedback(e.target.value)
                  e.target.style.height = "auto"
                  e.target.style.height = `${Math.min(e.target.scrollHeight, 96)}px`
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault()
                    handleReviseSubmit()
                  }
                  if (e.key === "Escape") {
                    setReviseMode(false)
                    setReviseFeedback("")
                  }
                }}
              />
              <Button
                className="size-7 shrink-0"
                disabled={!reviseFeedback.trim()}
                size="icon"
                variant="ghost"
                onClick={handleReviseSubmit}
              >
                <SendHorizonal className="size-4" />
              </Button>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex items-center justify-between px-4 py-3">
            <Button
              className="text-muted-foreground"
              size="sm"
              variant="ghost"
              onClick={() => onPlanDecision?.("cancel")}
            >
              {t("deepExplorer.reject")}
            </Button>
            <div className="flex items-center gap-2">
              {/* UI-4: execution-mode dropdown — surfaces the agent's
                      pick and lets the user override before approving.
                      Only renders when the planner has populated the
                      directive; for older plans it stays hidden. */}
              {(() => {
                const base = planEdits ?? file.content
                const planMode = parseExecutionMode(base)
                const selected = executionModeOverride ?? planMode
                if (!planMode) return null
                return (
                  <div className="text-muted-foreground flex items-center gap-1 text-xs">
                    <span className="hidden sm:inline">
                      {t("deepExplorer.execution_mode_label", {
                        defaultValue: "Mode",
                      })}
                    </span>
                    <Select
                      value={selected ?? planMode}
                      onValueChange={(next) => {
                        if (!EXECUTION_MODES.includes(next as ExecutionMode)) return
                        setExecutionModeOverride(next as ExecutionMode)
                      }}
                    >
                      <SelectTrigger
                        className="h-auto w-auto gap-1 rounded-md px-1.5 py-0.5 text-xs shadow-none"
                        aria-label={t("deepExplorer.execution_mode_aria", {
                          defaultValue: "Execution mode",
                        })}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {EXECUTION_MODES.map((mode) => (
                          <SelectItem key={mode} value={mode}>
                            {t(`deepExplorer.execution_mode_options.${mode}`, {
                              defaultValue: mode,
                            })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle
                            className="text-muted-foreground size-3 cursor-help"
                            aria-label={t("deepExplorer.execution_mode_help_aria", {
                              defaultValue: "What do these modes mean?",
                            })}
                          />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[320px]" side="top">
                          <p className="text-xs">
                            <strong>
                              {t("deepExplorer.execution_mode_options.reuse_scout", {
                                defaultValue: "Reuse scout",
                              })}
                              :
                            </strong>{" "}
                            {t("deepExplorer.execution_mode_descriptions.reuse_scout", {
                              defaultValue:
                                "Synthesize the deliverable from sources already retrieved during planning. No new searches, no parallel workers. Fastest.",
                            })}
                          </p>
                          <p className="mt-1 text-xs">
                            <strong>
                              {t("deepExplorer.execution_mode_options.validate_once", {
                                defaultValue: "Validate once",
                              })}
                              :
                            </strong>{" "}
                            {t("deepExplorer.execution_mode_descriptions.validate_once", {
                              defaultValue:
                                "Run one retrieval call after approval to confirm a single claim, then write the deliverable. No parallel workers.",
                            })}
                          </p>
                          <p className="mt-1 text-xs">
                            <strong>
                              {t("deepExplorer.execution_mode_options.full_worker_execution", {
                                defaultValue: "Full execution",
                              })}
                              :
                            </strong>{" "}
                            {t("deepExplorer.execution_mode_descriptions.full_worker_execution", {
                              defaultValue:
                                "Dispatch workers in parallel per todo with broad post-approval retrieval. Most thorough, slowest.",
                            })}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                )
              })()}
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setReviseMode((v) => !v)
                  setTimeout(() => reviseInputRef.current?.focus(), 50)
                }}
              >
                {t("deepExplorer.revise")}
              </Button>
              {(() => {
                const composeApproved = (): { editedContent?: string } | undefined => {
                  let composed = planEdits
                  if (executionModeOverride) {
                    composed = setExecutionMode(composed ?? file.content, executionModeOverride)
                  }
                  return composed != null ? { editedContent: composed } : undefined
                }
                return (
                  <>
                    <Button
                      size="sm"
                      onClick={() => onPlanDecision?.("approve", composeApproved())}
                    >
                      {t("deepExplorer.approve_and_run")}
                    </Button>
                    {/* UI-7: Run in background — same approve, but the
                            parent moves the session into the runs panel
                            and clears the foreground chat for new work. */}
                    {onApproveInBackground && (
                      <Button
                        size="sm"
                        variant="outline"
                        title={t("deepExplorer.run_in_background_tooltip", {
                          defaultValue:
                            "Approve and let the run continue in the background. Free the chat for other questions.",
                        })}
                        onClick={() => onApproveInBackground(composeApproved())}
                      >
                        {t("deepExplorer.run_in_background", {
                          defaultValue: "Run in background",
                        })}
                      </Button>
                    )}
                  </>
                )
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen TipTap editor modal */}
      <PlanEditorModal
        content={file.content}
        open={editorOpen}
        onCancel={() => setEditorOpen(false)}
        onSave={(md) => {
          setEditorOpen(false)
          onPlanDecision?.("approve", { editedContent: md })
        }}
      />
    </section>
  )
}
